import type { BlockCommandDependencies } from './BlockBaseCommand';
import { BlockBaseCommand } from './BlockBaseCommand';
import { reassignSignatureSigneeViaSocket } from '../grid/reduxStore/saveHandlers';
import { updateGridSignatureBlockState, selectBlockContent } from '../grid/reduxStore/editorSlice';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import type { AssignedSignee } from '../../../services/entities/SignatureTypes';
import { sanitizeAssignedSigneeForReassign } from '../../../services/entities/SignatureTypes';
import { rootStore } from '../grid/reduxStore/Store';

export class SignatureReassignCommand extends BlockBaseCommand {
  private readonly blockId: string;
  private readonly sectionId: string;
  private readonly oldSignee: AssignedSignee | null;
  private readonly newSignee: AssignedSignee | null;

  constructor(
    blockId: string,
    sectionId: string,
    oldSignee: AssignedSignee | null,
    newSignee: AssignedSignee | null,
    dependencies: BlockCommandDependencies
  ) {
    super(dependencies);
    this.blockId = blockId;
    this.sectionId = sectionId;
    this.oldSignee = oldSignee;
    this.newSignee = newSignee;
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }

  async undo(): Promise<boolean> {
    return this.applySignee(this.oldSignee);
  }

  async redo(): Promise<boolean> {
    return this.applySignee(this.newSignee);
  }

  private async applySignee(signee: AssignedSignee | null): Promise<boolean> {
    try {
      this.dispatchSigneeUpdate(signee);
      this.setSaving();

      const response = await reassignSignatureSigneeViaSocket(this.socketClient, {
        signatureBoxId: this.blockId,
        assignedSignee: sanitizeAssignedSigneeForReassign(signee),
      });

      this.dispatch(setSaveStatusBasedOnApiResponse(response));

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatchSigneeUpdate(signee === this.newSignee ? this.oldSignee : this.newSignee);
        return false;
      }

      if (response.status === AcknowledgmentResponseStatus.OK) {
        return true;
      }

      this.dispatchSigneeUpdate(signee === this.newSignee ? this.oldSignee : this.newSignee);
      return false;
    } catch {
      this.setNotSaved();
      this.dispatchSigneeUpdate(signee === this.newSignee ? this.oldSignee : this.newSignee);
      return false;
    }
  }

  private dispatchSigneeUpdate(signee: AssignedSignee | null) {
    const state = rootStore.getState();
    const blockContent = selectBlockContent(state, this.sectionId, this.blockId);

    if (blockContent?.contentSignature) {
      this.dispatch(
        updateGridSignatureBlockState({
          sectionId: this.sectionId,
          blockId: this.blockId,
          contentSignature: {
            ...blockContent.contentSignature,
            assignedSignee: signee,
          },
        })
      );
    }
  }
}
